import { CallDetailsFragment } from "@/graphql/generated/graphql";
import { AssetsCallType, Call as CustomCall, TranscriptionStatus } from "@/states/call/call.utility.decl";
import { mapToParticipant } from "@/states/call/participants/participants.utility";
import { calculateReadingTime } from "@/states/call/reading_time/reading_time.utility";
import { mapToSummary } from "@/states/call/summary/summary.utility";
import { mapToUtteranceSentiments } from "@/states/call/utterance_sentiments/utterance_sentiments.utility";
import { mapToUtterances } from "@/states/call/utterances/utterances.utility";
import { AssetType } from "../path_param/path_param.decl";
import { mapToKeyTopics } from "./key_topics/key_topics.utility";
import { mapToNextActions } from "./next_actions/next_actions.utility";
import { mapToOverallSentiments } from "./overall_sentiment/overall_sentiment.utility";
import { mapToSpeakers } from "./speakers/speakers.utility";
import { computeTalkRatio } from "./talk_ratio/talk_ratio.utility";

export const getDirection = (
  direction: CallDetailsFragment["direction"] | null
) => (direction === "INBOUND" ? "inbound" : "outbound");
// We need to check the direction, and we can't trust only the "type" since for inbound call the type is "CALLED" and not "ANSWERED"

export const getAssetsCallType = (
  type: CallDetailsFragment["type"] | null,
  direction: CallDetailsFragment["direction"] | null
): AssetsCallType =>
  type === "VOICEMAIL" ? "voicemail" : getDirection(direction);

export const getAssetType = (type: AssetsCallType): AssetType =>
  type === "voicemail" ? "voicemail" : "recording";

/**
 * The function responsible for mapping the backend data of a call to a Call entity which will serve the UI.
 * @param data
 */
export const mapToCall = (
  data: CallDetailsFragment | null
): CustomCall | null => {
  if (!data) {
    return null;
  }

  return {
    uuid: data.callUuid,
    id: data.callId,
    createdAt: data.createdAt,
    direction: getDirection(data.direction),
    type: getAssetsCallType(data.type, data.direction),
    from: mapToParticipant(data.from),
    to: mapToParticipant(data.to),
    answeredAt: data.answeredAt ?? undefined,
    utterances: mapToUtterances(data.transcription),
    nextActions: mapToNextActions(data.transcription),
    transcription: data.transcription,
    durationInSecond: data.durationInSecond,
    transcriptStatus: data.transcription
      ? (data.transcription.status as TranscriptionStatus)
      : undefined,
    speakers: mapToSpeakers(data.transcription, {
      from: data.from,
      to: data.to,
    }),
    keyTopics: mapToKeyTopics(data.transcription),
    summary: mapToSummary(data.transcription),
    readingTime: calculateReadingTime(data.transcription),
    talkRatio: computeTalkRatio(data.transcription?.content?.utterances),
    overallSentiments: mapToOverallSentiments(data.transcription),
    utteranceSentiments: mapToUtteranceSentiments(data.transcription),
    automaticCallbackCallId: data.automaticCallbackCallId,
    callbackRequestTimestamp: data.callbackRequestTimestamp,
  };
};
